/**
 * Embed widget theme presets — base packs that partners pick from in the
 * portal Edit dialog. Custom overrides layer on top of the chosen preset.
 *
 * The shape lines up with what `partner_embedded_instances.theme` JSONB stores:
 * `{ preset, colors, radius, font, mode, launcher }`. The bootstrap and the
 * iframe SPA read this and translate it to CSS vars.
 *
 * Keep these values in sync with `wunderguide-web/src/index.css` brand
 * variables — those are the canonical WG-branded palette, the embed app just
 * mirrors them.
 */
import { colors as wgColors } from './tokens';

export type EmbedThemePresetId =
  | 'wg-branded'
  | 'light'
  | 'dark'
  | 'ocean'
  | 'forest'
  | 'sunset'
  | 'midnight'
  | 'arctic';

/**
 * Display metadata for each preset — drives the portal's theme picker. Kept
 * here (not in the portal) so RN/web/embed all read from the same source.
 */
export const EMBED_THEME_META: Record<
  EmbedThemePresetId,
  { label: string; description: string }
> = {
  'wg-branded': {
    label: 'WG Branded',
    description: 'Magenta gradient · WunderGuide signature',
  },
  light: {
    label: 'Light',
    description: 'Clean white · indigo accent · best for daytime/professional',
  },
  dark: {
    label: 'Dark',
    description: 'Slate dark · soft indigo · easy on the eyes',
  },
  ocean: {
    label: 'Ocean',
    description: 'Deep navy · cyan accent · calm, premium',
  },
  forest: {
    label: 'Forest',
    description: 'Evergreen · sage accent · warm, grounded',
  },
  sunset: {
    label: 'Sunset',
    description: 'Warm cream · coral accent · hospitality, daytime',
  },
  midnight: {
    label: 'Midnight',
    description: 'True black · electric magenta · luxury, nightlife',
  },
  arctic: {
    label: 'Arctic',
    description: 'Icy mist · glacier blue · minimal, cool',
  },
};

export type LauncherShape = 'circle' | 'pill';
export type LauncherPosition =
  | 'bottom-right'
  | 'bottom-left'
  | 'top-right'
  | 'top-left';
export type ColorScheme = 'light' | 'dark' | 'auto';

export interface EmbedColors {
  /** Primary CTA / accent color */
  primary: string;
  /** Foreground on `primary` (usually white or dark) */
  primaryContrast: string;
  /** Whole-widget background */
  bg: string;
  /** Glass surface — message bubbles, tab bar, chip backgrounds. Translucent
   *  for presets that want a frosted-over-bg look (e.g. wg-branded). */
  surface: string;
  /**
   * Opaque content panel for cards. When omitted, falls back to `surface`.
   * Defined separately so dark/glass presets can keep a translucent
   * `surface` for chrome (tab bar, bubbles) while still giving cards
   * a solid readable background.
   */
  cardSurface?: string;
  /** Body text */
  text: string;
  /** Secondary / muted text */
  textMuted: string;
  /** Dividers + chip borders */
  border: string;
  /** Optional gradient for headers / launcher fills (CSS gradient string) */
  gradient?: string;
  /**
   * Urgent / "needs attention" accent — used by nav-badge dots, error
   * states, alert banners. Distinct from `primary` so partners with a
   * primary in the warm-warning hue range (Sunset orange, etc.) still
   * have a clearly distinguishable alert tone. Falls back to a sensible
   * red when omitted.
   */
  alert?: string;
}

/**
 * Compact collapsed-launcher presentation: a pill with the agent's avatar in a
 * circular badge on the left and a localized "Ask {name}" label to the right.
 *
 * WHY A SEPARATE OBJECT and not just shape:'pill': the existing pill is a
 * static, partner-labelled shape. Compact is a RESPONSIVE presentation of the
 * same launcher — an instance keeps its 2x animated avatar circle on desktop
 * and collapses to the pill on phones (`when:'mobile'`), where 120px of face
 * costs too much viewport. The bootstrap re-evaluates on host resize.
 *
 * The label is localized by the SPA at resolve time ("Ask Leilani" /
 * 「レイラニに質問」…) from the visitor's locale + the instance persona's
 * display name, and injected as `askLabel` — the same pattern as
 * `agentName`. Partners may pin a fixed string via `label`, or per-locale via
 * the `compact_label` row in partner_embedded_instance_strings; the built-in
 * localized template is the default so most instances configure nothing.
 */
export interface EmbedLauncherCompact {
  /** When the compact pill replaces the configured launcher. Default 'never'. */
  when?: 'never' | 'mobile' | 'desktop' | 'always';
  /**
   * Avatar treatment INSIDE the pill badge. 'inherit' (default) follows the
   * launcher's avatarMode, so a partner with an animated circle gets an
   * animated badge. 'animated' forces the video clip in the badge (Michael's
   * "animated avatar on mobile" option) even when the big launcher is static.
   */
  avatarMode?: 'inherit' | 'icon' | 'static' | 'animated';
  /**
   * Pill chrome. 'theme' (default) uses the launcher background/foreground.
   * The glass presets exist for hosts whose own chrome is glass/blur (wg-
   * explore's HUD) — backdrop-filter over the HOST page, so the pill frosts
   * whatever the site renders behind it.
   */
  style?: 'theme' | 'glass-light' | 'glass-dark' | 'solid';
  /** Last-wins fine-tuning on top of the style preset. */
  overrides?: {
    background?: string;
    foreground?: string;
    /** backdrop blur radius in px (glass styles default 14). */
    blur?: number;
    /** full CSS border value, e.g. '1px solid rgba(255,255,255,0.2)'. */
    border?: string;
    /** full CSS box-shadow value. */
    shadow?: string;
  };
  /** Fixed label override; beats the localized template when set. */
  label?: string;
  /**
   * SPA-injected localized "Ask {name}" (NOT partner-authored — same
   * contract as launcher.agentName).
   */
  askLabel?: string;
}

export interface EmbedLauncher {
  shape: LauncherShape;
  position: LauncherPosition;
  /** Pixel offset from the chosen corner */
  offsetX: number;
  offsetY: number;
  /** Pill-only: text shown next to the icon. Empty/undefined = icon only. */
  label?: string;
  /** Optional override of background color/gradient (else uses theme.colors.gradient || colors.primary) */
  background?: string;
  /** Foreground (icon/text) color override */
  foreground?: string;
  /** SVG path or 'default' for the chat-bubble icon */
  iconUrl?: string;
  /**
   * Agent's 1:1 still avatar (poster). Origin: agents.avatar_poster_url.
   * Used for avatarMode='static' and as the <video> poster in 'animated'.
   */
  avatarPosterUrl?: string;
  /** Agent's MP4 clip for avatarMode='animated'. Origin: agents.avatar_video_url. */
  avatarVideoUrl?: string;
  /** Agent's VP9 WebM alt, same clip. Origin: agents.avatar_video_webm. */
  avatarVideoWebm?: string;
  /**
   * Render mode the partner instance picked for the collapsed launcher.
   * Partners choose the mode; they do not override the agent's asset URLs.
   *   - 'icon'     : themed SVG/masked-icon (today's default). Avatar URLs
   *                  are ignored even if the agent has them.
   *   - 'static'   : <img src=avatarPosterUrl>, no animation.
   *   - 'animated' : <video> when avatarVideoUrl is set; otherwise the
   *                  poster with a CSS micro-breath (graceful degrade so
   *                  partners get motion before we've shot a real clip).
   * When undefined, the bootstrap treats it as 'icon' — partners must
   * explicitly opt in to showing an agent avatar.
   */
  avatarMode?: 'icon' | 'static' | 'animated';
  /**
   * Size multiplier on the circle launcher when `avatarMode` is non-icon
   * AND `avatarPosterUrl` is set. The face needs canvas to read as a
   * greeting; small avatars look like compressed thumbnails. Pill mode
   * and icon mode ignore this (their footprints are fixed).
   *   - '1x' : 60px (matches icon-mode footprint — smallest avatar size)
   *   - '2x' : 120px (default; introduced 2026-05-14 to make the face
   *           recognisable on first paint)
   * Undefined → '2x' so existing partners keep the larger avatar that
   * shipped when the feature went live.
   */
  avatarSize?: '1x' | '2x';
  /**
   * How the launcher's suggested-question pills are presented when the
   * widget is collapsed. Content comes from partner_embedded_instance_strings;
   * this controls the *form* of the rendering only.
   *   - 'stack'         : all up-to-3 chips stacked above the launcher
   *                       (today's default). Highest density / visual load.
   *   - 'rotate-bubble' : single speech-bubble with a tail pointing at the
   *                       launcher; chips cycle via smooth vertical scroll
   *                       (5s dwell, 400ms transition), auto-stops after
   *                       N cycles, pauses on hover/focus. prefers-
   *                       reduced-motion falls back to stack.
   * Undefined → 'stack' for backward compatibility.
   */
  suggestionsMode?: 'stack' | 'rotate-bubble';
  /**
   * Per-instance toggle (theme.launcher.showName): show the agent's display
   * name as a small glass nametag pill attached to the COLLAPSED circle
   * launcher — mirrors the open panel's identity pill so the guide is
   * introduced before the visitor ever opens the widget. Circle launchers
   * only (pill shape already carries a text label). Default off.
   */
  showName?: boolean;
  /**
   * The name the nametag renders. NOT partner-authored theme config — the
   * SPA injects the resolved display name (instance persona.display_name
   * override, the same source the open-panel header uses) before posting
   * the launcher to the bootstrap.
   */
  agentName?: string;
  /**
   * Hide the collapsed launcher entirely (display:none — the element stays
   * in the DOM and `WunderGuide.open()` / host-page buttons still work).
   * For hosts that provide their own entry point (e.g. explore's trip FAB)
   * and don't want the floating avatar competing with it. Also suppresses
   * the nametag and suggestion pills (they're launcher chrome). Default off.
   */
  hidden?: boolean;
  /** Responsive compact-pill presentation; see EmbedLauncherCompact. */
  compact?: EmbedLauncherCompact;
}

export type PanelMode = 'corner' | 'sidebar' | 'modal';
export type SidebarSide = 'right' | 'left';

/**
 * Cap on runtime layout promotion above the partner-configured `mode`:
 *   - 'native'    : disable expand/maximize entirely; the panel stays at its
 *                   configured `mode` (corner/sidebar/modal). No auto-expand
 *                   on checkout, no header toggle.
 *   - 'expanded'  : allow auto-promotion to a centered ~1040×760 modal during
 *                   checkout, plus a header toggle. Default.
 *   - 'maximized' : also allow the header toggle to reach near-fullscreen
 *                   (90vw × 90vh).
 */
export type LayoutMax = 'native' | 'expanded' | 'maximized';

export interface EmbedPanel {
  /**
   * How the open widget panel is laid out:
   *   - corner  : floating 380×560 anchored to the launcher's corner (default)
   *   - sidebar : full-viewport-height drawer pinned to one side
   *   - modal   : centered overlay with a dimmed backdrop
   */
  mode: PanelMode;
  /** Sidebar-only: which edge the drawer attaches to. */
  side?: SidebarSide;
  /** Override panel width (px). Defaults: corner=380, sidebar=400, modal=520. */
  width?: number;
  /** Override panel height (px). Corner-only — sidebar is full height; modal auto-sizes. */
  height?: number;
  /** Cap on runtime layout promotion. Default 'expanded' when undefined. */
  layoutMax?: LayoutMax;
}

export interface EmbedTheme {
  preset: EmbedThemePresetId;
  mode: ColorScheme;
  colors: EmbedColors;
  /** Border radius scale in px applied to surfaces (chips, cards). */
  radius: number;
  font: {
    family: string;
    /** Multiplier 0.9..1.2 */
    scale: number;
  };
  launcher: EmbedLauncher;
  /** How the open chat panel is positioned/sized in the parent viewport. */
  panel: EmbedPanel;
  /**
   * Layout of the open-chat title row (agent identity + close affordance).
   *   - 'centered-extended' (default, 2026-05): iOS-Messages-style — large
   *     centered avatar sitting at the top of the chat panel with a glassy
   *     name pill below it; close button absolute top-right. The avatar's
   *     top edge sits flush with the panel top, with a slight visual
   *     overhang via negative margin so it reads as the "focal" element.
   *   - 'linear-left': Compact left-aligned avatar + name on one row,
   *     close button on the right. The pre-2026-05 layout — partners that
   *     want a tighter header keep this.
   * Undefined → centered-extended.
   */
  headerLayout?: 'centered-extended' | 'linear-left';
}

const DEFAULT_PANEL: EmbedPanel = { mode: 'corner' };

const SATOSHI_STACK =
  "'Satoshi', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const WG_GRADIENT = `linear-gradient(135deg, ${wgColors.bgGradientStart} 0%, ${wgColors.bgGradientEnd} 100%)`;

/**
 * Default launcher shape for every preset — circle in bottom-right, 24px
 * inset. Each preset overrides only the colors that differ.
 */
const baseLauncher = {
  shape: 'circle' as const,
  position: 'bottom-right' as const,
  offsetX: 24,
  offsetY: 24,
};

export const EMBED_THEME_PRESETS: Record<EmbedThemePresetId, EmbedTheme> = {
  // Signature WunderGuide gradient — purple→magenta. Keep as the brand
  // statement for partners who want full WG identity.
  'wg-branded': {
    preset: 'wg-branded',
    mode: 'dark',
    colors: {
      // Embed surfaces (user bubble, send button) deliberately use the
      // darker wunderguide.com brand purple rather than the lighter app
      // primary (#aa12ff) — the bubble sits on a translucent surface over
      // the WG gradient bg, and the dark tone reads as "WG-native" instead
      // of competing with the gradient.
      primary: '#69005b',
      primaryContrast: '#ffffff',
      bg: wgColors.bgGradientStart, // #2c003e
      // surface is the GLASS chrome — tab bar, message bubbles, chip
      // backdrops. Translucent so they read as "floating over the WG
      // gradient" rather than carved-out solid blocks.
      surface: 'rgba(255, 255, 255, 0.08)',
      // cardSurface is the OPAQUE content panel — cards need a real
      // readable background so titles/desc don't smear over the
      // gradient. Solid darker-purple, a hair lighter than bg, keeps
      // it WG-native without going translucent.
      cardSurface: '#3a0a51',
      text: 'rgba(255, 255, 255, 0.96)',
      textMuted: 'rgba(255, 255, 255, 0.66)',
      border: 'rgba(255, 255, 255, 0.14)',
      gradient: WG_GRADIENT,
      alert: '#ff5b8a',
    },
    radius: 14,
    font: { family: SATOSHI_STACK, scale: 1 },
    launcher: {
      ...baseLauncher,
      background: WG_GRADIENT,
      foreground: '#ffffff',
    },
    panel: DEFAULT_PANEL,
  },

  // Clean professional light. Indigo (NOT purple) primary so it reads as
  // "trustworthy/professional" rather than "playful." Surface is a barely-
  // tinted off-white so bubbles sit visibly on the page bg.
  light: {
    preset: 'light',
    mode: 'light',
    colors: {
      primary: '#4338ca', // indigo-700 — confident, passes 4.5:1 on white
      primaryContrast: '#ffffff',
      bg: '#ffffff',
      surface: '#f5f6fa', // 4% off-white — bubbles visible without competing
      text: '#0f1419',
      textMuted: '#6b7280',
      border: '#e5e7eb',
      alert: '#dc2626',
    },
    radius: 12,
    font: { family: SATOSHI_STACK, scale: 1 },
    launcher: { ...baseLauncher, background: '#4338ca', foreground: '#ffffff' },
    panel: DEFAULT_PANEL,
  },

  // Clean dark — neutral slate, soft indigo accent. Deliberately NOT pure
  // black (#0a — keeps eye fatigue down). Primary is a desaturated indigo
  // so text on the user-bubble doesn't scream.
  dark: {
    preset: 'dark',
    mode: 'dark',
    colors: {
      primary: '#818cf8', // indigo-400 — bright enough on dark bg
      primaryContrast: '#0f1014',
      bg: '#0f1419',
      surface: 'rgba(255, 255, 255, 0.05)',
      text: 'rgba(255, 255, 255, 0.94)',
      textMuted: 'rgba(255, 255, 255, 0.62)',
      border: 'rgba(255, 255, 255, 0.1)',
      alert: '#f87171',
    },
    radius: 12,
    font: { family: SATOSHI_STACK, scale: 1 },
    launcher: { ...baseLauncher, background: '#1f2937', foreground: '#818cf8' },
    panel: DEFAULT_PANEL,
  },

  // Deep navy → cyan. Premium "calm" feel — good for travel concierge,
  // luxury hotels, anything where you want the chat to feel quiet.
  ocean: {
    preset: 'ocean',
    mode: 'dark',
    colors: {
      primary: '#22d3ee', // cyan-400 — bright on navy
      primaryContrast: '#06283d',
      bg: '#06283d',
      surface: 'rgba(34, 211, 238, 0.08)',
      text: 'rgba(255, 255, 255, 0.95)',
      textMuted: 'rgba(186, 230, 253, 0.66)',
      border: 'rgba(34, 211, 238, 0.18)',
      gradient: 'linear-gradient(135deg, #06283d 0%, #0891b2 100%)',
      alert: '#fb7185',
    },
    radius: 14,
    font: { family: SATOSHI_STACK, scale: 1 },
    launcher: {
      ...baseLauncher,
      background: 'linear-gradient(135deg, #0891b2 0%, #22d3ee 100%)',
      foreground: '#ffffff',
    },
    panel: DEFAULT_PANEL,
  },

  // Evergreen + sage. Earthy, organic — for outdoor/nature/wellness brands.
  // Forest Floor palette (Phase 7 of embed-pilot-readiness, 2026-05-25):
  // a true forest green primary on the existing deep-green bg, balanced
  // with a warm-bark accent. The previous palette was monochromatic-green
  // (lime primary + green-tinted text on green bg = muddy/swampy) — the
  // bark accent gives the warm/cool contrast WG and Dark have via their
  // accent-to-neutral pairings.
  forest: {
    preset: 'forest',
    mode: 'dark',
    colors: {
      primary: '#6dcc7c', // true forest green (was lime #a3e635)
      primaryContrast: '#ffffff', // white text on green button reads cleaner than dark-on-lime
      bg: '#1a2e1a', // unchanged — the forest-green anchor users associate with the theme
      surface: 'rgba(255, 255, 255, 0.06)',
      text: '#f0f0e8', // neutral warm white (was green-tinted) — kills the monotone
      textMuted: 'rgba(155, 181, 157, 0.82)', // sage-gray, still readable on dark green
      border: 'rgba(255, 255, 255, 0.14)',
      gradient: 'linear-gradient(135deg, #1a2e1a 0%, #3d5a3d 100%)',
      alert: '#d4a574', // warm bark/log accent — the forest-floor wood
    },
    radius: 12,
    font: { family: SATOSHI_STACK, scale: 1 },
    launcher: {
      ...baseLauncher,
      background: 'linear-gradient(135deg, #1a2e1a 0%, #6dcc7c 100%)',
      foreground: '#ffffff',
    },
    panel: DEFAULT_PANEL,
  },

  // Warm cream + coral/orange. Hospitality, daytime tourism, beach/island.
  // Light mode but distinctly warm rather than neutral.
  sunset: {
    preset: 'sunset',
    mode: 'light',
    colors: {
      primary: '#ea580c', // orange-600 — vivid but not eye-watering
      primaryContrast: '#ffffff',
      bg: '#fff7ed', // amber-50, warm cream
      surface: '#fff1e0', // a notch deeper than bg
      text: '#3f1d0a',
      textMuted: '#9a3412',
      border: '#fed7aa',
      gradient: 'linear-gradient(135deg, #fb923c 0%, #ec4899 100%)',
      alert: '#dc2626',
    },
    radius: 14,
    font: { family: SATOSHI_STACK, scale: 1 },
    launcher: {
      ...baseLauncher,
      background: 'linear-gradient(135deg, #fb923c 0%, #ec4899 100%)',
      foreground: '#ffffff',
    },
    panel: DEFAULT_PANEL,
  },

  // True black + electric magenta. Premium / nightlife / luxury — the
  // "Apple keynote" of widget themes.
  midnight: {
    preset: 'midnight',
    mode: 'dark',
    colors: {
      primary: '#d946ef', // fuchsia-500
      primaryContrast: '#000000',
      bg: '#000000',
      surface: 'rgba(255, 255, 255, 0.05)',
      text: 'rgba(255, 255, 255, 0.96)',
      textMuted: 'rgba(255, 255, 255, 0.6)',
      border: 'rgba(217, 70, 239, 0.22)',
      gradient: 'linear-gradient(135deg, #000000 0%, #6b21a8 100%)',
      alert: '#fb7185',
    },
    radius: 16,
    font: { family: SATOSHI_STACK, scale: 1 },
    launcher: {
      ...baseLauncher,
      background: 'linear-gradient(135deg, #6b21a8 0%, #d946ef 100%)',
      foreground: '#ffffff',
    },
    panel: DEFAULT_PANEL,
  },

  // Icy minimal — pale blue-white, glacier accent. For brands that want
  // chat to feel like a quiet ambient overlay rather than a CTA.
  arctic: {
    preset: 'arctic',
    mode: 'light',
    colors: {
      primary: '#0284c7', // sky-600
      primaryContrast: '#ffffff',
      bg: '#f0f9ff', // sky-50, frost-tinted
      surface: '#e0f2fe', // sky-100
      text: '#0c4a6e', // sky-900
      textMuted: '#0369a1',
      border: '#bae6fd',
      alert: '#dc2626',
    },
    radius: 12,
    font: { family: SATOSHI_STACK, scale: 1 },
    launcher: { ...baseLauncher, background: '#0284c7', foreground: '#ffffff' },
    panel: DEFAULT_PANEL,
  },
};

export const DEFAULT_EMBED_THEME: EmbedTheme =
  EMBED_THEME_PRESETS['wg-branded'];

/**
 * Resolve a partial theme override (as stored in DB.theme jsonb) against its
 * named preset. Result is always a complete EmbedTheme.
 */
export function resolveEmbedTheme(stored: unknown): EmbedTheme {
  const t = (stored ?? {}) as Partial<EmbedTheme> & {
    preset?: EmbedThemePresetId;
  };
  const preset =
    t.preset && EMBED_THEME_PRESETS[t.preset]
      ? EMBED_THEME_PRESETS[t.preset]
      : DEFAULT_EMBED_THEME;
  return {
    ...preset,
    ...t,
    colors: { ...preset.colors, ...(t.colors || {}) },
    font: { ...preset.font, ...(t.font || {}) },
    launcher: { ...preset.launcher, ...(t.launcher || {}) },
    panel: { ...preset.panel, ...(t.panel || {}) },
  };
}

/**
 * Translate a resolved theme into a flat map of CSS custom properties.
 * Used by the iframe SPA's <style> tag and by the bootstrap to style the
 * launcher button.
 */
export function themeToCssVars(theme: EmbedTheme): Record<string, string> {
  return {
    '--wg-color-primary': theme.colors.primary,
    '--wg-color-primary-contrast': theme.colors.primaryContrast,
    '--wg-color-bg': theme.colors.bg,
    '--wg-color-surface': theme.colors.surface,
    // Cards consume --wg-color-card-surface so glass presets can keep a
    // translucent --wg-color-surface for chrome (tab bar, bubbles)
    // without making cards unreadable. Falls back to surface when the
    // preset doesn't declare a separate panel color.
    '--wg-color-card-surface': theme.colors.cardSurface || theme.colors.surface,
    '--wg-color-text': theme.colors.text,
    '--wg-color-text-muted': theme.colors.textMuted,
    '--wg-color-border': theme.colors.border,
    '--wg-color-gradient': theme.colors.gradient || theme.colors.primary,
    // Urgent / "needs attention" tone for badges, error banners, etc.
    // Falls back to a neutral red when a preset hasn't declared one.
    '--wg-color-alert': theme.colors.alert || '#d94c4c',
    '--wg-radius': `${theme.radius}px`,
    '--wg-font-family': theme.font.family,
    '--wg-font-scale': String(theme.font.scale),
    '--wg-color-scheme': theme.mode,
  };
}
